import { useEffect } from "react";
import Navbar from "../components/Navbar/Navbar";
import "./LegalPage.css";

const LAST_UPDATED = "August 2025";
const ORG = "FORGE NGO";
const EMAIL = "info@forge.ngo";
const SITE = "forge.ngo";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div className="legal">
        <div className="legal__hero">
          <div className="container-fg container-2xxl">
            <p className="legal__eyebrow">Legal</p>
            <h1 className="legal__title">Privacy Policy</h1>
            <p className="legal__meta">Last updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <div className="legal__body container-fg container-2xxl">
          <div className="legal__intro">
            <p>
              {ORG} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard information when you visit <strong>{SITE}</strong>{" "}
              or interact with our member portal.
            </p>
          </div>

          <section className="legal__section">
            <h2>1. Information We Collect</h2>
            <h3>Information you provide directly</h3>
            <ul>
              <li>
                <strong>Application data</strong> — name, email, phone, date of birth, address, service history, and personal narrative when you apply for a retreat.
              </li>
              <li>
                <strong>Emergency contact information</strong> — name, phone, and relationship of your designated emergency contact.
              </li>
              <li>
                <strong>Documents</strong> — identification or service verification documents you upload.
              </li>
              <li>
                <strong>Communications</strong> — messages you send us through the portal or by email.
              </li>
              <li>
                <strong>Donation information</strong> — name, email, and payment details when you make a donation (payment data is processed by Stripe and never stored on our servers).
              </li>
              <li>
                <strong>Newsletter sign-ups</strong> — email address when you subscribe to updates.
              </li>
            </ul>
            <h3>Information collected automatically</h3>
            <ul>
              <li>Browser type and version, IP address, and referring URL for security and fraud prevention purposes only.</li>
              <li>We do not use tracking cookies, advertising pixels, or third-party analytics scripts.</li>
            </ul>
          </section>

          <section className="legal__section">
            <h2>2. How We Use Your Information</h2>
            <p>We use your information solely to:</p>
            <ul>
              <li>Review and process your retreat application.</li>
              <li>Communicate with you about your application status and retreat details.</li>
              <li>Process donations and send tax receipts.</li>
              <li>Send newsletters and updates you have opted into (you may unsubscribe at any time).</li>
              <li>Verify eligibility and maintain safety at our retreats.</li>
              <li>Meet legal and regulatory obligations as a 501(c)(3) public charity.</li>
            </ul>
            <p>We will never sell, rent, or trade your personal information to third parties.</p>
          </section>

          <section className="legal__section">
            <h2>3. How We Share Your Information</h2>
            <p>We share your information only in the following limited circumstances:</p>
            <ul>
              <li>
                <strong>Service providers</strong> — Stripe (payment processing) and our email provider (transactional emails only). These providers are contractually bound to protect your data.
              </li>
              <li>
                <strong>Legal requirements</strong> — when required by law, court order, or governmental authority.
              </li>
              <li>
                <strong>Safety</strong> — to protect the safety of retreat participants, staff, or the public.
              </li>
              <li>
                <strong>With your consent</strong> — for any other purpose, only with your explicit written consent.
              </li>
            </ul>
          </section>

          <section className="legal__section">
            <h2>4. Data Retention</h2>
            <p>
              We retain application and participant records for seven (7) years to comply with IRS requirements for 501(c)(3) organisations. Newsletter subscriber data is retained until you
              unsubscribe. You may request deletion of your data at any time (subject to our legal retention obligations) by emailing <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
            </p>
          </section>

          <section className="legal__section">
            <h2>5. Data Security</h2>
            <p>
              All data is transmitted over HTTPS. Passwords are hashed using bcrypt and never stored in plain text. Sensitive documents are stored in access-controlled directories and are not publicly
              accessible. Access to applicant data is restricted to authorised FORGE NGO staff only.
            </p>
          </section>

          <section className="legal__section">
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate information.</li>
              <li>Request deletion of your data (subject to legal retention obligations).</li>
              <li>Withdraw consent for communications at any time.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
            </p>
          </section>

          <section className="legal__section">
            <h2>7. Children's Privacy</h2>
            <p>
              Our retreats and services are designed for adults (18+). We do not knowingly collect personal information from anyone under 18. If you believe a minor has submitted information to us,
              please contact us immediately.
            </p>
          </section>

          <section className="legal__section">
            <h2>8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify registered applicants by email of any material changes. Continued use of our services after changes constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section className="legal__section">
            <h2>9. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy, please contact:
              <br />
              <strong>FORGE NGO</strong>
              <br />
              5203 Preserve Park Dr, Spring, TX 77389
              <br />
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
